// @flow

import { map as _map } from 'lodash';
import { pipe, prop, mapValues, uniq, includes, countBy, sumBy } from 'lodash/fp';

import { type DetailResult, type Order } from './types';
import { Pizza, type Topping, Toppings, ingredientName } from './pizza';
import { Random } from '../../../../utils/Random';
import {
  customerPreferences,
  dislikeBasedOrderStrings,
  genderBasedOrderStrings,
  simpleOrderString,
  toppingOpinionString,
  toppingKindOrderString,
} from './constants';
import { impossible } from './utils';

type ToppingKindOrderType = $Keys<typeof toppingKindOrderString>;
type GenderBasedOrderType = $Keys<typeof genderBasedOrderStrings>;
type DislikeBasedOrderType = $Keys<typeof dislikeBasedOrderStrings>;

class ToppingKindOrder implements Order {
  type: ToppingKindOrderType;

  constructor(type: ToppingKindOrderType) {
    this.type = type;
  }

  toString(): string {
    return toppingKindOrderString[this.type];
  }

  fitScore(pizza: Pizza): number {
    const toppingCountByType = countBy(topping => topping.type, pizza.toppings);
    switch (this.type) {
      case 'vegetable':
        return toppingCountByType['vegetable'] || 0;
      case 'meat':
        return toppingCountByType['meat'] || 0;
      case 'balance': {
        const vegetableScore = toppingCountByType['vegetable'] ? 1 : 0;
        const meatScore = toppingCountByType['meat'] ? 1 : 0;
        const seafoodScore = toppingCountByType['seafood'] ? 1 : 0;
        return meatScore + seafoodScore + vegetableScore;
      }
      default:
        return impossible(this.type);
    }
  }

  fitScoreDetail(pizza: Pizza): DetailResult {
    const totalScore = this.fitScore(pizza);
    const likedToppings = pizza.toppings.filter(topping => topping.type === this.type);
    const avgScore = likedToppings.length ? totalScore / likedToppings.length : 0;

    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * avgScore)
    )(likedToppings);

    return _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);
  }

  diagnose(pizza: Pizza) {
    const like: Array<any> = uniq(pizza.toppings.filter(topping => topping.type === this.type));
    return {
      like,
      dislike: [],
    };
  }
}

class GenderBasedOrder implements Order {
  type: GenderBasedOrderType;

  constructor(type: GenderBasedOrderType) {
    this.type = type;
  }

  toString(): string {
    return genderBasedOrderStrings[this.type];
  }

  fitScore(pizza: Pizza): number {
    switch (this.type) {
      case 'adult':
        return sumBy(n => (pizza.hasTopping(n) ? 1 : 0), customerPreferences.adult);
      case 'child':
        return sumBy(n => (pizza.hasTopping(n) ? 1 : 0), customerPreferences.child);
      default:
        return impossible(this.type);
    }
  }

  fitScoreDetail(pizza: Pizza): DetailResult {
    const totalScore = this.fitScore(pizza);
    const likedToppings = pizza.toppings.filter(topping =>
      customerPreferences[this.type].some(name => topping.topping === name)
    );
    const avgScore = likedToppings.length ? totalScore / likedToppings.length : 0;

    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * avgScore)
    )(likedToppings);

    return _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);
  }
}

class DislikeBasedOrder implements Order {
  type: DislikeBasedOrderType;

  constructor(type: DislikeBasedOrderType) {
    this.type = type;
  }

  toString(): string {
    return dislikeBasedOrderStrings[this.type];
  }

  fitScore(pizza: Pizza): number {
    const toppingCountByType = countBy(prop('type'), pizza.toppings);
    switch (this.type) {
      case 'vegetable':
        return 3 - (toppingCountByType['vegetable'] || 0);
      case 'meat':
        return 3 - (toppingCountByType['meat'] || 0);
      case 'no': {
        return 3;
      }
      default:
        return impossible(this.type);
    }
  }

  fitScoreDetail(pizza: Pizza): DetailResult {
    const totalScore = this.fitScore(pizza);
    const dislikedToppings = pizza.toppings.filter(topping => topping.type === this.type);
    const otherToppings = pizza.toppings.filter(topping => topping.type !== this.type);
    const avgScore = dislikedToppings.length ? (totalScore - 3) / dislikedToppings.length : 0;

    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * avgScore)
    )(dislikedToppings);

    //  all other toppings counted as liked ones
    for (const t of otherToppings) {
      toppingScores[t.topping] = 1;
    }

    return _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);
  }
}

type ToppingOrderType = {
  toppings: Topping[],
  kind: 'like' | 'dislike',
};

class ToppingOrder implements Order {
  type: ToppingOrderType;

  constructor(type: ToppingOrderType) {
    this.type = type;
  }

  toString(): string {
    const topping = this.type.toppings
      .map(ingredientName)
      .map(name => `${name}`)
      .join('と ');
    const opinion = toppingOpinionString(this.type.kind);
    return topping + opinion;
  }

  fitScore(pizza: Pizza): number {
    const toppingCount = this.type.toppings.length;
    const existingToppingCount = this.type.toppings.filter(topping => includes(topping, pizza.toppings)).length;
    if (this.type.kind === 'like') {
      return toppingCount === existingToppingCount ? 3 : existingToppingCount;
    } else {
      return existingToppingCount === 0 ? 3 : 2 - existingToppingCount;
    }
  }

  fitScoreDetail(pizza: Pizza): DetailResult {
    const totalScore = this.fitScore(pizza);
    const toppingScore = this.type.kind === 'like' ? totalScore : totalScore - 3;
    const targetToppings = this.type.toppings.filter(topping => pizza.hasTopping(topping.topping));
    const nontargetToppings = pizza.toppings.filter(topping => this.type.toppings.every(t => t !== topping));
    const avgScore = targetToppings.length ? toppingScore / targetToppings.length : 0;

    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * avgScore)
    )(targetToppings);

    // in the dislike case, all other toppings counted as liked ones
    if (this.type.kind === 'dislike') {
      for (const t of nontargetToppings) {
        toppingScores[t.topping] = 1;
      }
    }

    return _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);
  }
}

class SimpleOrder implements Order {
  toString(): string {
    return simpleOrderString;
  }

  fitScore(pizza: Pizza): number {
    const toppingCount = pizza.toppings.length;
    if (toppingCount > 3) return 0;
    return 3 - toppingCount;
  }

  fitScoreDetail(pizza: Pizza): DetailResult {
    const toppingScores = pipe(
      countBy('topping'),
      mapValues(count => count * -1)
    )(pizza.toppings);

    return _map(toppingScores, (count: number, toppingName: string) => [Toppings[toppingName], count]);
  }
}

const toppingKindOrderTypes: ToppingKindOrderType[] = Object.keys(toppingKindOrderString);
const randomToppingKindOrder: Random<Order> = Random.of(...toppingKindOrderTypes).map(
  type => new ToppingKindOrder(type)
);

const genderBasedOrderTypes: GenderBasedOrderType[] = Object.keys(genderBasedOrderStrings);
const randomGenderBasedOrder: Random<Order> = Random.of(...genderBasedOrderTypes).map(
  type => new GenderBasedOrder(type)
);

const dislikeBasedOrderTypes: DislikeBasedOrderType[] = Object.keys(dislikeBasedOrderStrings);
const randomDislikeBasedOrder: Random<Order> = Random.of(...dislikeBasedOrderTypes).map(
  type => new DislikeBasedOrder(type)
);

const randomTopping: Random<any> = Random.of(...Object.values(Toppings));
const randomToppingOrderType = Random.array(randomTopping, 1, 2)
  .filter(toppings => toppings.length < 2 || toppings[0] !== toppings[1])
  .bind(toppings => Random.of('like', 'dislike').map(kind => ({ toppings, kind })));
const randomToppingOrder = randomToppingOrderType.map(type => new ToppingOrder(type));

//const randomSimpleOrder: Random<Order> = Random.const(new SimpleOrder());

// account for bias caused by number of order in each type
const simpleOrderThreshold = 0;
const toppingKindOrderThreshold = simpleOrderThreshold + toppingKindOrderTypes.length;
const genderBasedOrderThreshold = toppingKindOrderThreshold + genderBasedOrderTypes.length;
const dislikeBasedOrderThreshold = genderBasedOrderThreshold + dislikeBasedOrderTypes.length;
const toppingOrderThreshold = dislikeBasedOrderThreshold + 4; // [1 topping, 2 toppings] * [ like, dislike ]

const OrderGenerator: Random<Order> = Random.int(1, toppingOrderThreshold).bind(i => {
  if (i <= toppingKindOrderThreshold) {
    return randomToppingKindOrder;
  } else if (i <= genderBasedOrderThreshold) {
    return randomGenderBasedOrder;
  } else if (i <= dislikeBasedOrderThreshold) {
    return randomDislikeBasedOrder;
  } else {
    return randomToppingOrder;
  }
});

export { SimpleOrder, GenderBasedOrder, DislikeBasedOrder, ToppingKindOrder, ToppingOrder, OrderGenerator };
